import { BirtvyWebSocialBody } from '~/design-system/components/DeckGen/birtvy/render/BirtvyWebCard';
import { hostnameOf, type BirtvyWeb } from '~/design-system/components/DeckGen/birtvy/web/birtvyWeb';
import { markStageFailed } from './stageSignals';

export interface WebsiteBodyProps {
  web: BirtvyWeb;
  /** The row key, so a screenshot that 404s records itself as this item's failure. */
  itemKey: string;
}

/**
 * A website on the stage: the post, the live page, or the capture — in that order.
 *
 * The order is the canvas's (`BirtvyWebCard`'s `CardBody`) and it is not arbitrary. A `post` means X or
 * Instagram, which block framing AND screenshotting, so the backend extracted the post itself and there
 * is nothing else to show. `frameable` means the site allows iframing, and the live page beats a
 * capture of it. Everything else is the capture.
 *
 * `BrowserBar` — the URL, the live dot, reload, open-external — is the pane's, drawn from the `url`
 * and `live` that `stageFor` returns. Nothing in here draws chrome.
 */
export function WebsiteBody({ web, itemKey }: WebsiteBodyProps) {
  const label = web.title?.trim() || hostnameOf(web.url);

  /**
   * A social post. `autoResize={false}` is what the fullscreen viewer passes: the auto-resize exists to
   * push a measured height back onto the canvas card, and this surface has no card to resize — the
   * frame is already the post's own box. Rendered, not screenshotted, and the avatars and media inside
   * it ride the same same-origin proxy the canvas uses, because those CDNs refuse a cross-origin `img`.
   */
  if (web.post) {
    return (
      <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
        <BirtvyWebSocialBody web={web} autoResize={false} />
      </div>
    );
  }

  /**
   * A live page. This one keeps its pointer events — the pane has drawn a browser bar above it with a
   * reload and an open-in-a-new-tab, so it reads as a browser and a browser that ignores the mouse
   * would be a lie. Same sandbox as the canvas card: `allow-same-origin` is present because this is a
   * REAL third-party origin loaded by URL, where it grants the page nothing it does not already have —
   * the opposite of an artifact's `srcDoc`, which inherits ours.
   */
  if (web.frameable) {
    return (
      <iframe
        src={web.url}
        title={label}
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#ffffff' }}
      />
    );
  }

  /**
   * A capture. Screenshots are FULL PAGE and the frame is a viewport-shaped box, so the picture is
   * pinned to the width and the rest of the page is a scroll away — the same arrangement as the card
   * and the viewer, which is why the fold lands in the same place in all three.
   */
  return (
    <div style={{ width: '100%', height: '100%', overflowY: 'auto', overscrollBehavior: 'contain', background: '#ffffff' }}>
      <img
        src={web.screenshotUrl}
        alt={label}
        draggable={false}
        onError={() => markStageFailed(itemKey)}
        style={{ width: '100%', display: 'block', userSelect: 'none' }}
      />
    </div>
  );
}
